"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProgramService = exports.ProgramServiceEvent = void 0;
const rbm_ott_sdk_1 = require("@ericssonbroadcastservices/rbm-ott-sdk");
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
exports.ProgramServiceEvent = {
    NEW_PROGRAMS: "new_programs",
    PROGRAM_CHANGED: "program_changed",
    NOT_ENTITLED: "not_entitled",
    BLACKOUT: "blackout",
    EMPTY_SLOT: "empty_slot",
};
class ProgramService extends js_player_shared_1.EmitterBaseClass {
    constructor({ asset, play, baseUrl, customer, businessUnit, sessionToken, }) {
        var _a, _b, _c;
        super();
        this.epgEnabled = false;
        this.pingState = "IDLE";
        this.storedPrograms = [];
        this.shouldCheckEntitlement = false;
        // Spread pre-entitlement requests within 2 minutes from start time
        this.preEntitleTreshold = 2 * 60 * 1000 * Math.random();
        this.preEntitlementRequests = new WeakMap();
        this.context = {
            customer,
            businessUnit,
            baseUrl,
        };
        this.sessionToken = sessionToken;
        // If we're playing a channel, use that ID - otherwise find the program's channel
        const channelAssetId = asset.type === "TV_CHANNEL" ? asset.assetId : (_a = play.streamInfo) === null || _a === void 0 ? void 0 : _a.channelId;
        if (!channelAssetId)
            return;
        this.channelAssetId = channelAssetId;
        this.epgEnabled = !!((_b = play.epg) === null || _b === void 0 ? void 0 : _b.enabled);
        this.shouldCheckEntitlement = Boolean((_c = play.epg) === null || _c === void 0 ? void 0 : _c.entitlementCheck);
    }
    setPingStateIfProgramEnded(currentTime) {
        const endTime = this.watching && new Date(this.watching.endTime).getTime();
        const isProgramEnded = endTime && currentTime >= endTime;
        if (this.isSingleEventChannel() && isProgramEnded) {
            this.pingState = "IDLE";
        }
    }
    ping(currentTime) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Set this.pingState to "IDLE" if this channel has one single program and currentTime is equal to or greater than this.watching.endTime
            this.setPingStateIfProgramEnded(currentTime);
            if (["DISABLED", "REQUESTING"].includes(this.pingState) ||
                currentTime === -1) {
                return;
            }
            const position = this.epgEnabled ? currentTime : undefined;
            // Check against the stored data
            let currentProgram = this.getCurrentProgram(position);
            let upcomingProgram = this.getUpcomingProgram(position);
            // Check if we should pre-entitle
            if (this.shouldCheckEntitlement &&
                upcomingProgram &&
                position &&
                !this.preEntitlementRequests.has(upcomingProgram) &&
                this.preEntitleTreshold >
                    new Date(upcomingProgram.startTime).getTime() - position) {
                this.preEntitlementRequests.set(upcomingProgram, this.isEntitled(upcomingProgram));
            }
            if (((_a = this.watching) === null || _a === void 0 ? void 0 : _a.startTime) &&
                this.watching.startTime === (currentProgram === null || currentProgram === void 0 ? void 0 : currentProgram.startTime)) {
                return;
            }
            this.pingState = "REQUESTING";
            // update the stored data if there is no current or upcoming program
            if (!currentProgram || !upcomingProgram) {
                yield this.updatePrograms();
                currentProgram = this.getCurrentProgram(position);
                upcomingProgram = this.getUpcomingProgram(position);
            }
            // If currentProgram exist and this channel has one single program, set this.pingState to "DISABLED" and call this.handleProgramChanged
            if (currentProgram && this.isSingleEventChannel()) {
                this.pingState = "DISABLED";
                yield this.handleProgramChanged(currentProgram, upcomingProgram);
                return;
            }
            // If there's still no programs, disable the ProgramService for 30s
            // Report no current program, i.e. only channel will be reported from the player
            if (!currentProgram) {
                this.handleNoProgram();
                this.pingState = "DISABLED";
                this.disabledTimeout = window.setTimeout(() => {
                    this.pingState = "IDLE";
                }, 30000);
            }
            else {
                yield this.handleProgramChanged(currentProgram, upcomingProgram);
                this.pingState = "IDLE";
            }
        });
    }
    isSingleEventChannel() {
        return this.epgEnabled && this.storedPrograms.length === 1;
    }
    handleNoProgram() {
        // If we've are watching something that has data, but suddenly get a lack of data
        if (this.epgEnabled && this.watching) {
            this.emit(exports.ProgramServiceEvent.EMPTY_SLOT, undefined);
        }
        this.emit(exports.ProgramServiceEvent.PROGRAM_CHANGED, {
            currentProgram: undefined,
            upcomingProgram: undefined,
        });
        this.watching = undefined;
    }
    handleProgramChanged(currentProgram, upcomingProgram) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentProgram) {
                return this.handleNoProgram();
            }
            this.watching = currentProgram;
            if ("blackout" in currentProgram && currentProgram.blackout) {
                this.emit(exports.ProgramServiceEvent.BLACKOUT, currentProgram);
            }
            else if (yield this.isEntitled(currentProgram)) {
                this.emit(exports.ProgramServiceEvent.PROGRAM_CHANGED, {
                    currentProgram,
                    upcomingProgram,
                });
            }
            else {
                console.warn("User is not entitled to the current program", currentProgram);
                this.emit(exports.ProgramServiceEvent.NOT_ENTITLED, currentProgram);
            }
        });
    }
    getCurrentProgram(position) {
        const now = position || new Date().getTime();
        return this.storedPrograms.find((program) => new Date(program.startTime).getTime() < now &&
            new Date(program.endTime).getTime() > now);
    }
    getUpcomingProgram(position) {
        const now = position || new Date().getTime();
        return this.storedPrograms.find((program) => new Date(program.startTime).getTime() > now);
    }
    updatePrograms() {
        return __awaiter(this, void 0, void 0, function* () {
            const programs = yield this.getPrograms();
            if (programs.length === 0) {
                return;
            }
            programs.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
            this.storedPrograms = programs;
            this.emit(exports.ProgramServiceEvent.NEW_PROGRAMS, { programs });
        });
    }
    isEntitled(program) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.shouldCheckEntitlement) {
                return true;
            }
            const preEntitlementCheck = this.preEntitlementRequests.get(program);
            if (preEntitlementCheck) {
                const status = yield preEntitlementCheck;
                this.preEntitlementRequests.delete(program);
                return status;
            }
            // For channels without epg we check entitlement on channel level
            // For channels with epg we check entitlement on program level
            const assetId = this.epgEnabled
                ? (_a = program.asset) === null || _a === void 0 ? void 0 : _a.assetId
                : this.channelAssetId;
            if (!assetId) {
                return false;
            }
            const time = new Date(Math.max(new Date().getTime(), 
            // For pre-entitlements we need to add one millisecond, because startTime is often publication time,
            // and the request fails unless time is after publication time
            new Date(program.startTime).getTime() + 1)).toISOString();
            try {
                const headers = { Authorization: `Bearer ${this.sessionToken}` };
                yield rbm_ott_sdk_1.entitle.call(this.context, { assetId, time, headers });
                return true;
            }
            catch (_b) {
                return false;
            }
        });
    }
    getPrograms() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.channelAssetId) {
                console.warn("[ProgramService] Failed to get programs, no channel assetId found");
                return [];
            }
            try {
                if (this.epgEnabled && this.channelAssetId) {
                    return (yield rbm_ott_sdk_1.getEpgForChannel.call(this.context, {
                        channelId: this.channelAssetId,
                        date: new Date(),
                        daysForward: 0,
                        daysBackward: 0,
                        pageSize: 500,
                        pageNumber: 1,
                    })).programs;
                }
                // for channels without an epg configured, we fallback to the channel status (a.k.a. "on now") endpoint.
                // This only gets a few programs per request, so we append them to the storedPrograms
                const { assets = [] } = yield rbm_ott_sdk_1.getChannelStatus.call(this.context, {
                    channelId: this.channelAssetId,
                    minutesForward: 100,
                });
                const storedStartTimes = this.storedPrograms.map((p) => p.startTime);
                const nextPrograms = assets.filter((p) => !storedStartTimes.includes(p.startTime));
                return [...this.storedPrograms, ...nextPrograms];
            }
            catch (e) {
                console.warn("[ProgramService] Failed to get programs", e);
                return [];
            }
        });
    }
    destroy() {
        super.destroy();
        clearTimeout(this.disabledTimeout);
    }
}
exports.ProgramService = ProgramService;
//# sourceMappingURL=index.js.map